import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LinkIcon from "@mui/icons-material/Link";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  AdminAudienceViewDto,
  AdminReviewSessionViewDto,
  AdminSessionViewDto,
  AnyoneLinkStateDto,
  FlyerPublicationStatus,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { routes } from "../../../../utils/constant/navigationRoutes";
import { toastSuccess } from "../../../../utils/pageUtil/toast";
import { FlyerSnapshot } from "../useFlyerSnapshot";
import { WorkspaceStats } from "./ChangesByItem/useReviewActivity";
import { reviewTokens } from "../theme";
import { LazyThumb } from "./LazyThumb";

interface Props {
  flyerId: string;
  snapshot?: FlyerSnapshot;
  snapshotLoading: boolean;
  snapshotError: boolean;
  onRetrySnapshot: () => void;
  bootstrap?: AdminReviewSessionViewDto;
  bootstrapLoading: boolean;
  workspaceStats: WorkspaceStats;
  onManageAccess: () => void;
  onToggleFlyerComments: () => void;
  flyerCommentsOpen?: boolean;
  onOpenFullHistory?: () => void;
  itemListId?: string;
}

const fmtDate = (d?: Date) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "";
  }
};

const sessionStatusLabel = (
  status: AdminSessionViewDto.StatusEnum | undefined,
  t: TFunction,
): string => {
  switch (status) {
    case AdminSessionViewDto.StatusEnum.APPROVED:
      return t(messages.Review_Session_Status_Approved());
    case AdminSessionViewDto.StatusEnum.CLOSED:
      return t(messages.Review_Session_Status_Closed());
    case AdminSessionViewDto.StatusEnum.ACTIVE:
      return t(messages.Review_Session_Status_Active());
    default:
      return "";
  }
};

const publicationStatusLabel = (
  status: FlyerPublicationStatus | undefined,
): string => {
  if (!status) return "";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const sessionStatusColor = (
  status: AdminSessionViewDto.StatusEnum | undefined,
): "success" | "default" | "info" => {
  switch (status) {
    case AdminSessionViewDto.StatusEnum.APPROVED:
      return "success";
    case AdminSessionViewDto.StatusEnum.CLOSED:
      return "default";
    default:
      return "info";
  }
};

const anyoneAccessLabel = (
  access: AnyoneLinkStateDto.AccessEnum | undefined,
  t: TFunction,
): string => {
  switch (access) {
    case AnyoneLinkStateDto.AccessEnum.EDIT:
      return t(messages.Edit());
    case AnyoneLinkStateDto.AccessEnum.VIEW:
      return "View";
    default:
      return "—";
  }
};

const MetaCell = ({
  icon,
  label,
  value,
  title,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  title?: string;
}) => (
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    sx={{ minWidth: 0, maxWidth: "100%" }}
    title={title}
  >
    {icon ? <Box sx={{ flexShrink: 0, lineHeight: 0, display: "flex" }}>{icon}</Box> : null}
    <Typography
      component="span"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: reviewTokens.textMuted,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}:
    </Typography>
    <Box sx={{ minWidth: 0 }}>{value}</Box>
  </Stack>
);

export function FlyerMetaBar({
  flyerId,
  snapshot,
  snapshotLoading,
  snapshotError,
  onRetrySnapshot,
  bootstrap,
  bootstrapLoading,
  workspaceStats,
  onManageAccess,
  onToggleFlyerComments,
  flyerCommentsOpen = false,
  onOpenFullHistory,
  itemListId,
}: Props) {
  const { t } = useTranslation();

  const copyId = () => {
    void navigator.clipboard.writeText(flyerId);
    toastSuccess(t(messages.Copied()));
  };

  if (snapshotLoading || bootstrapLoading) {
    return (
      <Paper
        variant="outlined"
        data-testid="flyer-meta-bar"
        sx={{ p: 2, mb: 2 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="rounded" width={56} height={56} />
          <Box flex={1}>
            <Skeleton width="40%" height={22} />
            <Skeleton width="70%" height={16} sx={{ mt: 0.5 }} />
            <Skeleton width="55%" height={16} />
          </Box>
          <Skeleton variant="rounded" width={140} height={32} />
        </Stack>
      </Paper>
    );
  }

  if (snapshotError || !snapshot) {
    return (
      <Paper variant="outlined" data-testid="flyer-meta-bar" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography color="error" variant="body2">
            {t(messages.Review_Load_Flyer_Error())}
          </Typography>
          <Button size="small" onClick={onRetrySnapshot}>
            {t(messages.Review_Retry_Load_Flyer())}
          </Button>
        </Stack>
      </Paper>
    );
  }

  const session = bootstrap?.session;
  const anyone = bootstrap?.anyoneLink;
  const audiences = bootstrap?.audiences ?? [];
  const activeAudiences = audiences.filter(
    (a: AdminAudienceViewDto) =>
      a.permission !== AdminAudienceViewDto.PermissionEnum.REVOKED &&
      !a.revokedAt,
  ).length;

  const dateRange =
    snapshot.validStartDate && snapshot.validEndDate
      ? `${fmtDate(snapshot.validStartDate)} – ${fmtDate(snapshot.validEndDate)}`
      : "";

  const sessionLabel = sessionStatusLabel(session?.status, t);
  const publicationLabel = publicationStatusLabel(snapshot.publicationStatus);
  const showPublicationChip =
    Boolean(publicationLabel) &&
    (!sessionLabel ||
      publicationLabel.toLowerCase() !== sessionLabel.toLowerCase());
  const anyoneLinkEnabled =
    Boolean(anyone?.url) &&
    anyone?.access !== AnyoneLinkStateDto.AccessEnum.DISABLED;

  const metaValueSx = {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: reviewTokens.textPrimary,
    lineHeight: 1.4,
  } as const;

  const metaValueEllipsisSx = {
    ...metaValueSx,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  } as const;

  const metaIconSx = { fontSize: 15, color: reviewTokens.slate500 };

  const metaText = (
    text: string,
    opts?: { disabled?: boolean; ellipsis?: boolean; title?: string },
  ) => (
    <Tooltip title={opts?.title || text} disableHoverListener={!text || text === "—"}>
      <Typography
        variant="body2"
        component="span"
        sx={opts?.ellipsis ? metaValueEllipsisSx : metaValueSx}
        color={opts?.disabled ? "text.disabled" : undefined}
      >
        {text || "—"}
      </Typography>
    </Tooltip>
  );

  const metaItems: React.ReactNode[] = [
    <MetaCell
      key="session"
      icon={<EventOutlinedIcon sx={metaIconSx} />}
      label={t(messages.Review_Session_Label())}
      value={metaText(dateRange, { disabled: !dateRange, ellipsis: true })}
    />,
    <MetaCell
      key="merchant"
      icon={<BusinessOutlinedIcon sx={metaIconSx} />}
      label={t(messages.Merchant())}
      title={snapshot.merchantLabels || undefined}
      value={metaText(snapshot.merchantLabels, {
        disabled: !snapshot.merchantLabels,
        ellipsis: true,
        title: snapshot.merchantLabels,
      })}
    />,
    <MetaCell
      key="store"
      icon={<StorefrontOutlinedIcon sx={metaIconSx} />}
      label={t(messages.Store())}
      title={snapshot.storeLabels || undefined}
      value={metaText(snapshot.storeLabels, {
        disabled: !snapshot.storeLabels,
        ellipsis: true,
        title: snapshot.storeLabels,
      })}
    />,
    <MetaCell
      key="link"
      icon={<LinkIcon sx={metaIconSx} />}
      label={t(messages.Review_Anyone_Link())}
      value={
        anyoneLinkEnabled ? (
          <Link
            href={anyone?.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              ...metaValueSx,
              color: "primary.main",
              textDecorationColor: "transparent",
              "&:hover": { textDecorationColor: "currentColor" },
            }}
          >
            {anyoneAccessLabel(anyone?.access, t)}
          </Link>
        ) : (
          metaText(anyoneAccessLabel(anyone?.access, t), { disabled: true })
        )
      }
    />,
    <MetaCell
      key="reviewer"
      icon={<PeopleOutlineIcon sx={metaIconSx} />}
      label={t(messages.Reviewer())}
      value={metaText(
        t(messages.Review_Active_Audiences(), { count: activeAudiences }),
        { ellipsis: true },
      )}
    />,
  ];

  return (
    <Paper
      variant="outlined"
      data-testid="flyer-meta-bar"
      sx={{ mb: 2, overflow: "hidden" }}
    >
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ md: "center" }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: `${reviewTokens.radiusSm}px`,
                overflow: "hidden",
                border: `1px solid ${reviewTokens.border}`,
              }}
            >
              <LazyThumb src={snapshot.coverUrl} size={44} fit="cover" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                sx={{ rowGap: 0.25, columnGap: 0.75 }}
              >
                <Tooltip title={snapshot.pdfLabel || ""} placement="top-start">
                  <Typography
                    variant="h6"
                    component="h2"
                    noWrap
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: reviewTokens.textPrimary,
                      maxWidth: { xs: "100%", md: 420 },
                    }}
                  >
                    {snapshot.pdfLabel || t(messages.Review_Flyer_Context())}
                  </Typography>
                </Tooltip>
                {sessionLabel ? (
                  <Chip
                    size="small"
                    label={sessionLabel}
                    color={sessionStatusColor(session?.status)}
                    sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600 }}
                  />
                ) : null}
                {showPublicationChip ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={publicationLabel}
                    sx={{ height: 20, fontSize: "0.6875rem" }}
                  />
                ) : null}
                <Tooltip title={t(messages.Review_Copy_Flyer_Id())}>
                  <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    onClick={copyId}
                    sx={{
                      cursor: "pointer",
                      px: 0.5,
                      py: 0.125,
                      borderRadius: `${reviewTokens.radiusSm}px`,
                      border: `1px solid ${reviewTokens.border}`,
                      "&:hover": { bgcolor: reviewTokens.slate50 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: reviewTokens.fontMono,
                        fontSize: "0.625rem",
                        color: reviewTokens.textSecondary,
                      }}
                    >
                      {flyerId.slice(0, 8)}…
                    </Typography>
                    <ContentCopyIcon sx={{ fontSize: 11, color: reviewTokens.textMuted }} />
                  </Stack>
                </Tooltip>
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            flexShrink={0}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<PeopleOutlineIcon sx={{ fontSize: 15 }} />}
              onClick={onManageAccess}
              sx={{ py: 0.375, px: 1 }}
            >
              {t(messages.Who_Has_Access())}
            </Button>
            <Badge
              badgeContent={workspaceStats.openFlyerCommentCount}
              color="warning"
              invisible={!workspaceStats.openFlyerCommentCount}
              sx={{ "& .MuiBadge-badge": { fontSize: "0.625rem", height: 16, minWidth: 16 } }}
            >
              <Button
                size="small"
                variant={flyerCommentsOpen ? "contained" : "outlined"}
                color={flyerCommentsOpen ? "primary" : "inherit"}
                startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />}
                onClick={onToggleFlyerComments}
                data-testid="meta-bar-flyer-comments-button"
                sx={{ py: 0.375, px: 1 }}
              >
                {t(messages.Flyer_Comments())}
              </Button>
            </Badge>
            {onOpenFullHistory && workspaceStats.itemCount > 0 ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={onOpenFullHistory}
                data-testid="meta-bar-view-full-history"
                sx={{ py: 0.375, px: 1 }}
              >
                {t(messages.View_Full_History())}
              </Button>
            ) : null}
            <Button
              component="a"
              href={routes.uploadFlyer.editById(flyerId)}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
              sx={{ py: 0.375, px: 1 }}
            >
              {t(messages.Open_In_Flyer_Editor())}
            </Button>
            {itemListId ? (
              <Button
                component="a"
                href={routes.createFlyer.byId(itemListId)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                sx={{ py: 0.375, px: 1 }}
              >
                {t(messages.Open_In_Item_Editor())}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          sx={{
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${reviewTokens.border}`,
            columnGap: 0,
            rowGap: 0.75,
          }}
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", lg: "block" },
                mx: 1.25,
                borderColor: reviewTokens.border,
                height: 18,
                alignSelf: "center",
              }}
            />
          }
        >
          {metaItems}
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{
          px: 1.5,
          py: 0.625,
          borderTop: `1px solid ${reviewTokens.border}`,
          bgcolor: reviewTokens.slate50,
        }}
      >
        <Chip
          size="small"
          color={workspaceStats.pendingCrTotal ? "warning" : "default"}
          variant={workspaceStats.pendingCrTotal ? "filled" : "outlined"}
          sx={{ height: 22 }}
          label={t(messages.Review_Workspace_Pending(), {
            count: workspaceStats.pendingCrTotal,
          })}
        />
        <Chip
          size="small"
          variant="outlined"
          sx={{ height: 22 }}
          label={t(messages.Review_Workspace_Items(), {
            count: workspaceStats.itemCount,
          })}
        />
        <Chip
          size="small"
          variant="outlined"
          sx={{ height: 22 }}
          label={t(messages.Review_Workspace_Flyer_Comments(), {
            count: workspaceStats.openFlyerCommentCount,
          })}
        />
      </Stack>
    </Paper>
  );
}
